import React, { useEffect, useMemo, useState } from 'react'
import { useLoad } from '../shared/hooks/load'
import { useQueryParam } from '../shared/hooks/location'
import { AuthHead, AuthLoading, AuthNotice, AuthPage } from '../shared/sso-workspace/auth-card'
import { ReturnCard } from '../shared/sso-workspace/return-card'
import { useApiFormAuthProvider } from '../api-new/state/auth'
import { SignInLinkFailed, SignInLinkSpent, useSignInLinkSpent } from '../shared/sso-workspace/sign-in-link-spent'
import { fromDetails, RemovedMembershipNotice } from '../shared/sso-workspace/removed-memberships'

// /open/complete?token=…: the sign-in is done; on to the client's sign-in
// URL (redirect-signin-url: the admin's /open/auth, an app's edxr://auth
// deep link, or the web player's own page). The hand-off replaces this page
// in history (owner report 2026-10-13): Back or a restored tab must not come
// back to a token the app has already used. If it does anyway, the details
// call answers sign_in_link_used and the calm SignInLinkSpent card shows (or,
// with a session here, the admin). The Retry button is the person's own tap.
//
// A web return (http/https: the admin itself, or the web player, which signs
// in in its own tab since the client report of 2026-10-21) never says
// "returned to the app" or "close this window": the person is in the same
// tab, being taken back. Until the details answer, the page says only
// "Signing you in…", since it can't yet tell an app from a web page.
//
// A removed membership (spec 2026-10-23 SCIM R25): when this sign-in went
// on into an organization while one it would have joined had removed the
// person, the details carry removedOrganizations and the page says so in
// one line ("Your access to Acme was removed by an administrator. Ask them
// to restore it."). A web return then waits for Continue (still a replace),
// so the line is read before the page goes; an app's hand-off is unchanged
// and the line stays on this page behind it.
const isWebReturn = url => !!url && (url.protocol === 'https:' || url.protocol === 'http:')

export const OpenComplete = () => {
  const token = useQueryParam('token')

  const [error, updateError] = useState()

  const [hasRedirect, updateHasRedirect] = useState(false)

  const apiFormAuthProvider = useApiFormAuthProvider()

  const [spent, onSpent] = useSignInLinkSpent()
  // The link's first call failed otherwise: the "didn't work" card (owner 2026-10-17).
  const [failed, updateFailed] = useState(null)

  const [loaded, updateLoaded] = useState(false)

  useLoad(async () => {
    try {
      await apiFormAuthProvider.get(`${token}/details`)
      updateLoaded(true)
    } catch (error) {
      if (!onSpent(error)) updateFailed(error || {})
    }
  }, [])

  let url = useMemo(
    () => {
      if (apiFormAuthProvider.form.redirectSigninURL) {
        let url = new URL(apiFormAuthProvider.form.redirectSigninURL)
        url.searchParams.set('token', token)

        updateHasRedirect(true)

        return url
      }

      return null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiFormAuthProvider]
  )

  const removed = useMemo(() => fromDetails(apiFormAuthProvider.form), [apiFormAuthProvider.form])
  const holdForNotice = removed.length > 0 && isWebReturn(url)

  useEffect(
    () => {
      if (url && !holdForNotice) {
        try {
          window.location.replace(url.href)
        } catch (error) {
          updateError(error)
        }
      }
    },
    [url, holdForNotice]
  )

  const handleOpen = () => {
    window.location = url.href
  }

  // "Open the app" is a real link (it works without script, and a middle
  // click does nothing harmful); a plain click goes the same way as the
  // first hand-off, and a failure says so.
  const handleOpenLink = e => {
    e.preventDefault()
    try {
      window.location = url.href
      updateError(null)
    } catch (err) {
      updateError(err)
    }
  }

  // Continue past the notice: a replace, like every hand-off here.
  const handleContinue = () => {
    window.location.replace(url.href)
  }

  if (spent) {
    return <SignInLinkSpent reason={spent} />
  }

  if (failed) {
    return <SignInLinkFailed error={failed} />
  }

  if (holdForNotice) {
    return (
      <ReturnCard
        variant="signedInWeb"
        title="You're signed in"
        hint={false}
        action={<button type="button" className="btn sw-btn-primary sa-block" onClick={handleContinue}>Continue</button>}>
        <RemovedMembershipNotice entries={removed} />
      </ReturnCard>
    )
  }

  if (isWebReturn(url)) {
    return (
      <AuthPage labelledBy="complete-title">
        <AuthHead id="complete-title" title="Signing you in…" center />
        {error
          ? <>
              <AuthNotice tone="warn" role="alert">
                <p>We couldn't take you back to EducationXR automatically.</p>
              </AuthNotice>
              <button type="button" className="btn sw-btn-primary sa-block" onClick={handleOpen}>Continue</button>
            </>
          : <AuthLoading>Taking you back to EducationXR…</AuthLoading>}
      </AuthPage>
    )
  }

  if (!hasRedirect && !loaded) {
    return (
      <AuthPage labelledBy="complete-title">
        <AuthHead id="complete-title" title="Signing you in…" center />
        <AuthLoading>One moment…</AuthLoading>
      </AuthPage>
    )
  }

  // An app's sign-in (the hand-off is its deep link, edxr://auth): the
  // browser stays on this page behind the app. "Open the app" is the same
  // deep link again, for when the browser didn't hand off (or asked first
  // and the person said no).
  return hasRedirect
    ? <ReturnCard
        variant="signedIn"
        title="You're signed in"
        lede="Return to the EducationXR app — it's finishing up."
        action={<a className="btn sw-btn-primary sa-block" href={url.href} onClick={handleOpenLink}>Open the app</a>}>
        <RemovedMembershipNotice entries={removed} />
        {error &&
          <AuthNotice tone="warn" role="alert">
            <p>Your browser didn't open the EducationXR app. Choose Open the app to try again, or switch to the app yourself.</p>
          </AuthNotice>}
      </ReturnCard>
    : <ReturnCard
        variant="signedIn"
        title="You're signed in"
        lede="Return to the EducationXR app to finish signing in.">
        <RemovedMembershipNotice entries={removed} />
      </ReturnCard>
}
